import React, { useEffect, useRef, useState } from "react";
import ShopCard from "./shopcard";
import SectionWrapper from "../../components/sectionWrapper";
import ButtonContainer from "../../components/buttonContainer";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "../../../../component/SnackbarContext";
import {
  generateOrderPayload,
  generateOrderServices,
} from "../../../../utils/orderPayload";
import axiosInstance from "../../../../configuration/request";
import { clearOrderDetails } from "../../../../Redux/Slices/customerSlice";
import PaymentSetion from "./paymentSection";
import AddOns from "./addOns";
import CustomPopupForm from "../../../../component/customPopupModel";
import checkServiceType from "../../../../utils/commonFunction";


const ReviewAndPlaceOrder = () => {
  const [open, setOpen] = useState(false);
  const { orderFlow, pickupDetails, shopDetails, selectedService, payloadAddress, cartItems } = useSelector((state) => state.customer);
  const { loginDetails } = useSelector((state) => state.counter);
  


  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  
  const userId = loginDetails.userId || null;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [paymentBreakDown, setPaymentBreakDown] = useState({
    orderAmount: 0,
    campaignCode: null,
    discount: 0,
    amountAfterDiscount: 0,
    tax: 0,
    platformFee: 0,
    deliveryCharge: 0,
    finalPayable: 0,
  });


  const paymentRef = useRef(null);
  // Flag to track if we are in the process of leaving the page
  const isRedirecting = useRef(false);


  // Redirection Logic: Handle user coming back from Stripe
  useEffect(() => {
    const handlePageShow = (event) => {
      // event.persisted is true if the page was loaded from the bfcache (Back Button)
      if (event.persisted) {
        navigate("/customer-home");
      }
    };


    window.addEventListener("pageshow", handlePageShow);
    
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [navigate]);


  // Initialize Items
  useEffect(() => {
    // If we are redirecting, ignore state changes
    if (isRedirecting.current) return;

    if (cartItems.length > 0) {
      const services = generateOrderServices({
        cartItems,
        selectedService,
        shopDetails,
      });
      setOrderItems(services);
    } 
  }, [shopDetails, cartItems, selectedService]);


  // Handle Total Value and Payment Calculation
  useEffect(() => {
    // If shopDetails is cleared or we are redirecting, stop further calculations
    if (!shopDetails || isRedirecting.current) return;
    
    const basePrice = Number(shopDetails?.totalPrice) || 0;
    const addOnsTotal = selectedAddOns.reduce((sum, item) => {
      const priceNum = Number(item.price?.replace("$", "")) || 0;
      return sum + priceNum * (item.qty || 1);
    }, 0);


    const updatedTotalPrice = basePrice + addOnsTotal;
    setTotalValue(updatedTotalPrice);
    
    fetchPayment(updatedTotalPrice, paymentBreakDown.campaignCode || '');
  }, [shopDetails, selectedAddOns]);


  const fetchPayment = (totalPrice, code = '') => {
    // Guard against API calls after clearing order details
    if (isRedirecting.current) return;

    const serviceSelected = checkServiceType(selectedService);
    let url = `/customer/orders/calculate-breakdown?orderAmount=${totalPrice}&storeId=${shopDetails?.id}&serviceType=${serviceSelected}`;
    if (code && code.trim() !== '') {
      url += `&campaignCode=${code}`;
    }


    axiosInstance
      .get(url)
      .then((res) => {
        // Guard against updating state if we have already cleared details
        if (isRedirecting.current) return;

        setPaymentBreakDown({
          orderAmount: res?.data?.orderAmount,
          campaignCode: res?.data?.campaignCode || null,
          discount: res?.data?.discount || 0,
          amountAfterDiscount: res?.data?.amountAfterDiscount,
          tax: res?.data?.tax,
          platformFee: res?.data?.platformFee,
          deliveryCharge: res?.data?.deliveryCharge,
          finalPayable: res?.data?.finalPayable,
          campaignId: res?.data?.campaignId || null
        });
      })
      .catch((err) => {
        // If we are redirecting, suppress error messages as they are caused by missing/cleared state
        if (isRedirecting.current) return;

        if (code !== '') {
            fetchPayment(totalPrice, '');
            return;
        }
        showSnackbar(
          err?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  };


  const nextHandler = (totalToPay, tip) => {
    const payload = generateOrderPayload({
      pickupDetails,
      orderItems,
      shopDetails,
      payloadAddress,
      userId,
      paymentBreakDown,
      tip,
      totalToPay,
      selectedAddOns,
    });
    axiosInstance
      .post(`/customer/orders/create`, payload)
      .then((res) => {
        // Set the redirect flag to true BEFORE clearing the data
        isRedirecting.current = true;
        dispatch(clearOrderDetails());
        setIsLoading(true);
        setOpen(false);
        // Redirecting to Stripe
        window.location.href = res?.data?.checkout_url;
      })
      .catch((err) => {
        isRedirecting.current = false;
        showSnackbar(
          err?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  };


  const previousHandler = () => {
    if (orderFlow === "shop") {
      navigate("/add-to-cart");
    } else {
      navigate("/shop-selection");
    }
  };


  const handleAddOnSelection = (items) => {
    setSelectedAddOns(items);
  };


  return (
    <SectionWrapper title="Order" subTitle="Review">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="hidden lg:block mb-4">
          <ShopCard laundry={shopDetails} />
        </div>
        <div className="flex flex-col xl:flex-row gap-6 w-full ">
          <div className=" w-full xl:max-w-[330px]">
            <AddOns laundry={shopDetails} onSelectionChange={handleAddOnSelection}/>
          </div>
          <div ref={paymentRef} className="flex-1">
            <PaymentSetion
              storeId={shopDetails?.id}
              selectedService={selectedService}
              orderItems={orderItems[0]?.items}
              totalValue={totalValue}
              paymentBreakDown={paymentBreakDown}
              handleApplyCoupon={(code) => { fetchPayment(totalValue, code) }}
              laundry={shopDetails}
              selectedAddOns={selectedAddOns}
            />
          </div>
        </div>
        <div className="mt-6">
          <ButtonContainer
            handleNext={() => setOpen(true)}
            handlePrevious={previousHandler}
            next={"Proceed to Checkout"}
          />
        </div>
      </div>
      
      <CustomPopupForm
        isOpen={open}
        handleClose={() => setOpen(false)}
        dialogType="tip"
        dialogDetails={paymentBreakDown?.finalPayable}
        onSubmit={nextHandler}
      />
    </SectionWrapper>
  );
};


export default ReviewAndPlaceOrder;
